import { API, postJSON } from "../../api.js";
import { wireCommandButton } from "../../app/commands.js";
import { $, cls, esc, fmtMoney } from "../../ui/format.js";
import { helpIcon } from "../../ui/help.js";
import { toast } from "../../ui/modals.js";
import { openSellStakeModal } from "./government.js";

// ---------------------------------------------------------------- Investors & Cap Table

const CAPTABLE_CLASS_BY_TYPE = { Founder: "founder", ESOP: "esop", Investor: "investor", Public: "public" };

function _sentimentClass(verdict) {
  if (verdict === "Bullish") return "strong";
  if (verdict === "Constructive") return "satisfied";
  if (verdict === "Cautious") return "concerned";
  return "crisis"; // Bearish | Flight Risk
}

async function renderInvestors(main, snap) {
  main.innerHTML = `
    <h2 class="panel-title">Investors &amp; Cap Table — Month ${snap.month} ${helpIcon("investors")}</h2>
    <div class="plain-english-card" id="ir-plain-english">Loading…</div>
    <div id="ir-gauge">Loading…</div>

    <h3 style="font-family:var(--font-display); font-size:17px; margin:24px 0 12px;">What Moves Sentiment</h3>
    <div class="risk-row" style="display:block;">
      <div style="font-size:12.5px; color:var(--text-mid); line-height:1.7;">
        <strong style="color:var(--text-hi);">Valuation momentum (45%)</strong> — is your estimated valuation trending up or down vs. 3 months ago.<br>
        <strong style="color:var(--text-hi);">Net margin (25%)</strong> — are you actually profitable, not just growing.<br>
        <strong style="color:var(--text-hi);">Board confidence spillover (20%)</strong> — a Board in crisis rattles investors too.<br>
        <strong style="color:var(--text-hi);">Brand (10%)</strong> — reputation is a small but real factor.
      </div>
    </div>

    <div class="deal-actions" style="margin: 18px 0 22px; flex-wrap: wrap;">
      <button id="btn-investor-call">Hold Investor Call</button>
      <button id="btn-raise-capital">Raise Capital</button>
      <button id="btn-issue-dividend">Issue Dividend</button>
      <button class="primary" id="btn-sell-stake">Sell a Stake</button>
    </div>

    <h3 style="font-family:var(--font-display); font-size:17px; margin:8px 0 4px;">Cap Table</h3>
    <p style="font-size:12px; color:var(--text-mid); margin:0 0 4px;">Who owns the company right now, and what each stake is worth at today's estimated valuation.</p>
    <div id="captable-bar">Loading…</div>
    <div id="captable-list"></div>
    <div id="stake-sale-result" style="margin-top:20px;"></div>

    <h3 style="font-family:var(--font-display); font-size:17px; margin:24px 0 12px;">Funding History</h3>
    <div id="funding-history">Loading…</div>
  `;

  const ir = await fetch(`${API}/investor-relations`).then((x) => x.json());
  const cls = _sentimentClass(ir.verdict);
  const valuation = snap.ratios.valuation || 0;
  const stakeValue = valuation * (ir.founder_ownership_pct / 100);

  $("ir-plain-english").innerHTML =
    `Your stake is worth an estimated <strong style="color:var(--brass);">${fmtMoney(stakeValue)}</strong> right now
     (${ir.founder_ownership_pct}% of a ${fmtMoney(valuation)} company). Investors are feeling
     <strong>${esc(ir.verdict.toLowerCase())}</strong> about where this is headed — ${
       ir.investor_sentiment >= 55
         ? "your next raise would likely come on favorable terms."
         : "your next raise would likely cost you more dilution than it should. An investor call or a period of clean execution helps before you raise."
     }`;

  $("ir-gauge").innerHTML = `
    <div class="risk-stat-row">
      <div class="risk-stat-box">
        <div class="risk-stat-label">Investor Sentiment</div>
        <div class="risk-stat-value">${ir.investor_sentiment}<span style="font-size:14px;color:var(--text-low)">/100</span></div>
        <div class="confidence-gauge-track"><div class="confidence-gauge-fill ${cls}" style="width:${ir.investor_sentiment}%"></div></div>
        <div style="font-family:var(--font-mono); font-size:12px; color:var(--text-mid);">${esc(ir.verdict)}</div>
      </div>
      <div class="risk-stat-box">
        <div class="risk-stat-label">Founder Ownership</div>
        <div class="risk-stat-value">${ir.founder_ownership_pct}%</div>
      </div>
      <div class="risk-stat-box">
        <div class="risk-stat-label">ESOP Pool</div>
        <div class="risk-stat-value">${ir.esop_pool_pct}%</div>
      </div>
      <div class="risk-stat-box">
        <div class="risk-stat-label">Your Stake, Est. Value</div>
        <div class="risk-stat-value" style="font-size:19px;">${fmtMoney(stakeValue)}</div>
      </div>
    </div>
  `;

  $("btn-investor-call").addEventListener("click", async () => {
    const res = await postJSON("/investor-relations/call", {});
    await window.refreshAll();
    toast(res.message);
  });
  wireCommandButton($("btn-raise-capital"), "raise");
  wireCommandButton($("btn-issue-dividend"), "dividend");
  $("btn-sell-stake").addEventListener("click", openSellStakeModal);

  const entries = ir.cap_table.entries;
  $("captable-bar").innerHTML = `
    <div class="captable-bar-track">
      ${entries.map((e) => `<div class="captable-segment ${CAPTABLE_CLASS_BY_TYPE[e.holder_type] || "public"}" style="width:${e.ownership_pct}%" title="${esc(e.holder_name)}">${e.ownership_pct >= 8 ? e.ownership_pct + "%" : ""}</div>`).join("")}
    </div>
    <div class="captable-legend">
      ${entries.map((e) => `<span><span class="swatch" style="background:var(--${e.holder_type === "Founder" ? "brass" : e.holder_type === "ESOP" ? "sage" : e.holder_type === "Investor" ? "oxblood" : "text-low"})"></span>${esc(e.holder_name)} — ${e.ownership_pct}%</span>`).join("")}
    </div>
  `;
  $("captable-list").innerHTML = `
    <table class="ledger">
      <tr><th>Holder</th><th>Type</th><th>Ownership</th><th>Est. Value Today</th><th>Invested</th><th>Round Month</th></tr>
      ${entries.map((e) => `
        <tr>
          <td class="label">${esc(e.holder_name)}</td>
          <td class="mono">${esc(e.holder_type)}</td>
          <td class="mono">${e.ownership_pct}%</td>
          <td class="mono">${fmtMoney(valuation * (e.ownership_pct / 100))}</td>
          <td class="mono">${e.invested_amount ? fmtMoney(e.invested_amount) : "—"}</td>
          <td class="mono">M${e.round_month}</td>
        </tr>
      `).join("")}
    </table>
  `;

  const rounds = entries.filter((e) => e.holder_type !== "Founder").sort((a, b) => a.round_month - b.round_month);
  $("funding-history").innerHTML = rounds.length ? rounds.map((e) => `
    <div class="risk-row">
      <div class="risk-row-left">
        <div class="risk-row-label">Month ${e.round_month} — ${esc(e.holder_name)}</div>
        <div class="risk-row-sub">${esc(e.holder_type)}${e.invested_amount ? " · raised " + fmtMoney(e.invested_amount) : ""} · took ${e.ownership_pct}% of the company</div>
      </div>
    </div>
  `).join("") : `<p style="color:var(--text-mid)">No outside funding rounds yet — 100% founder-owned.</p>`;
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.
window.renderInvestors = renderInvestors;
